/**
 * F 129 Cut 2b — markdown -> Block[] parser.
 *
 * Closes the round-trip F 117 left open. Together:
 *
 *   F 117  Block[]  -> markdown   (serialize-blocks.ts)
 *   F 128  markdown -> HTML       (render.ts, Cut 1)
 *   F 129  markdown -> Block[]    (this file, Cut 2b)
 *
 *        Block[] -> markdown -> Block[]
 *
 * which is what makes batch migration and interop possible at all.
 *
 * Design rules, all load-bearing:
 *
 *  1. ONE parser. This uses `markdownItInstance` from render.ts — the same
 *     factory Cut 1 uses, carrying the same FCr inline plugin. A second
 *     markdown-it instance would mean a second rule table, and the plugin
 *     would exist in one and not the other. That is the fork d000697
 *     Principle 4 forbids.
 *
 *  2. NEVER THROWS. Unknown syntax degrades to a paragraph. A migration tool
 *     that throws halfway leaves the document half-converted, which is worse
 *     than a paragraph.
 *
 *  3. XSS-SAFE BY CONSTRUCTION. The shared instance runs with `html: false`,
 *     so raw HTML in the source is ESCAPED TEXT before it ever reaches a
 *     token. `html_inline` / `html_block` cannot appear, and nothing from the
 *     source can introduce markup into a Block. A round-trip therefore
 *     *reduces* injection surface: parsing an adversarial document and
 *     re-serialising it yields plain text.
 *
 *  4. DROPS ARE REPORTED, NEVER SILENT. Same `Drop` shape as F 117 so a
 *     consumer handles one union across both directions.
 */

import type Token from 'markdown-it/lib/token.mjs';
import { markdownItInstance, type RenderOptions } from './render';
import type { Block, BlockType, InlineNode } from '../../types/models/Block';
import { coerceInlineContent } from '../../types/models/Block';

// ─── Drops — mirrors F 117's shape (serialize-blocks.ts) ────────────────────

export type ParseDropReason =
  /** Nested list items flattened — markdown nesting is not recoverable from a flat pass. */
  | 'nested-list-flattened'
  /** A blockquote's inner blocks collapsed to a single quote block. */
  | 'blockquote-children-flattened'
  /** `toggleListItem` has no markdown marker; parsed as a bullet item. */
  | 'toggle-degraded-to-bullet'
  /** A table was parsed but a Block cannot carry its grid in this cut. */
  | 'table-degraded-to-rows'
  /** Footnote syntax is not parsed in Cut 2b; kept as paragraph text. */
  | 'footnote-not-parsed'
  /** A link's `title` has no Block field. */
  | 'link-title-unsupported'
  /** Inline HTML was escaped by `html:false` and survives as literal text. */
  | 'raw-html-escaped-to-text'
  /** A token the mapper did not recognise; degraded to a paragraph. */
  | 'unknown-token-degraded-to-paragraph';

export interface ParseDrop {
  /** The construct the loss applies to. */
  blockType: string;
  reason: ParseDropReason;
  /** Human-readable one-liner, safe to show in a UI notice. */
  message: string;
  /** How many occurrences in this document. */
  count: number;
}

/**
 * What the parser actually returns.
 *
 * `Block` (src/types/models/Block.ts) declares `content: BlockContent` — the
 * `{ richText?: RichText[] }` shape that nothing on the read or write path
 * uses. The real storage shape, which F 117 reads and the editor consumes, is
 * a BlockNote inline array plus a `props` bag. Declaring that here rather than
 * casting at every call site keeps the discrepancy visible in the type system
 * instead of hiding it. See README.md — reconciling the two is a follow-up.
 */
export interface ParsedBlock extends Omit<Block, 'content'> {
  /** BlockNote-shaped inline nodes: `{ type, text, styles, href? }`. */
  content: InlineNode[];
  /** Per-type data (`level`, `checked`, `language`, `url`, `name`).
   *  Mirrored onto `attributes` so both spellings are present. */
  props: Record<string, unknown>;
}

export interface ParseResult {
  /** Canonical F 116 blocks. Never empty when `markdown` was non-empty. */
  blocks: ParsedBlock[];
  /** Everything that could not be represented exactly. Never silent. */
  drops: ParseDrop[];
}

class DropLog {
  private readonly byKey = new Map<string, ParseDrop>();
  add(blockType: string, reason: ParseDropReason, message: string): void {
    const key = `${reason}::${blockType}`;
    const existing = this.byKey.get(key);
    if (existing) existing.count += 1;
    else this.byKey.set(key, { blockType, reason, message, count: 1 });
  }
  list(): ParseDrop[] { return Array.from(this.byKey.values()); }
}

// ─── Block construction ─────────────────────────────────────────────────────

/** Monotonic per-process id. Stable within a document, not across reloads —
 *  identity is a storage concern, not a parsing one. */
let seq = 0;
function nextId(): string {
  seq += 1;
  return `md-${seq.toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * `props` vs `attributes`.
 *
 * The canonical `Block` interface (src/types/models/Block.ts) declares
 * `attributes: Record<string, unknown>` and has no `props`. But F 117's
 * serialiser reads `block.props` for `level` and `checked`, and the shipped
 * page templates in pageContentStorage.ts use `props` too. So the parser
 * writes BOTH, keeping the round-trip closed against F 117 as it exists
 * today, while staying shaped like the declared type. The duplication is
 * deliberate and temporary; it is flagged in src/core/markdown/README.md.
 */
function makeBlock(
  type: BlockType,
  content: InlineNode[],
  props: Record<string, unknown> = {},
): ParsedBlock {
  return {
    id: nextId(),
    type,
    content,
    children: [],
    attributes: { ...props },
    createdAt: new Date(),
    updatedAt: new Date(),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ...({ props } as any),
  } as ParsedBlock;
}

// ─── Inline tokens -> RichText[] ────────────────────────────────────────────

/**
 * F 131 — `InlineNode` is declared ONCE, in the model layer
 * (`src/types/models/Block.ts`), and `Block.content` is typed as it. This
 * module re-exports the type so existing F 128 / F 129 consumers keep their
 * import path, but there is only one definition.
 *
 * It was declared here separately in F 129, which is precisely how the
 * parser and the model came to disagree about what a block's content is.
 * F 129's note that "the declared shape describes nothing that is stored"
 * was also wrong: the 4 factories in models/Block.ts and EnhancedPageEditor
 * used the declared shape. F 131 aligned the declaration to the data.
 */
export type { InlineNode };

const VOID = new Set([
  'softbreak', 'hardbreak', 'text', 'html_inline', 'code_inline', 'image',
]);

function inlineTokensToNodes(tokens: Token[]): InlineNode[] {
  const out: InlineNode[] = [];
  const marks: { bold: boolean; italic: boolean; strikethrough: boolean; code: boolean; link?: string }[] = [];
  let pending = '';

  const flush = () => {
    if (!pending) return;
    const top = marks[marks.length - 1];
    const styles = {
      ...(top?.bold ? { bold: true } : {}),
      ...(top?.italic ? { italic: true } : {}),
      ...(top?.strikethrough ? { strikethrough: true } : {}),
      ...(top?.code ? { code: true } : {}),
    };
    const node: InlineNode = { type: top?.link ? 'link' : 'text', text: pending, styles };
    if (top?.link) node.href = top.link;
    out.push(node);
    pending = '';
  };

  for (const t of tokens) {
    switch (t.type) {
      case 'text': pending += t.content; break;
      // html:false means raw HTML arrives here already escaped, so it stays text.
      case 'html_inline': pending += t.content; break;
      case 'softbreak': pending += '\n'; break;
      case 'hardbreak': pending += '\n'; break;
      case 'code_inline': flush(); out.push({ type: 'text', text: t.content, styles: { code: true } }); break;
      case 'strong_open': flush(); marks.push({ bold: true, italic: false, strikethrough: false, code: false }); break;
      case 'em_open': flush(); marks.push({ bold: false, italic: true, strikethrough: false, code: false }); break;
      case 's_open': flush(); marks.push({ bold: false, italic: false, strikethrough: true, code: false }); break;
      case 'link_open':
        flush();
        marks.push({ bold: false, italic: false, strikethrough: false, code: false, link: t.attrGet('href') ?? '' });
        break;
      case 'link_close': flush(); marks.pop(); break;
      case 'strong_close': case 'em_close': case 's_close': flush(); marks.pop(); break;
      default: break;
    }
  }
  flush();

  // merge adjacent runs carrying identical marks
  const merged: InlineNode[] = [];
  for (const n of out) {
    const last = merged[merged.length - 1];
    if (last && last.type === n.type && last.href === n.href
        && JSON.stringify(last.styles) === JSON.stringify(n.styles)) {
      last.text += n.text;
    } else merged.push({ ...n });
  }
  return merged;
}

const plainText = (nodes: InlineNode[]): string => nodes.map((n) => n.text).join('');

// ─── Block-level token walk ─────────────────────────────────────────────────

/** GitHub task-list prefix, the syntax F 117's serialiser emits. */
const TASK_RE = /^\[([ xX])\]\s+/;

export function parseMarkdownToBlocks(markdown: string): ParseResult {
  const drops = new DropLog();
  if (typeof markdown !== 'string' || markdown.length === 0) {
    return { blocks: [], drops: drops.list() };
  }

  let tokens: Token[];
  try {
    // THE SAME INSTANCE FAMILY AS CUT 1 — one parser (Principle 4).
    const md = markdownItInstance({} as RenderOptions);
    tokens = md.parse(markdown, {});
  } catch {
    // A parse failure must not lose the document. The escaped source becomes
    // one paragraph, which still round-trips as text.
    return {
      blocks: [makeBlock('paragraph', [{ type: 'text', text: markdown, styles: {} }])],
      drops: drops.list(),
    };
  }

  const blocks: ParsedBlock[] = [];
  /** list nesting depth, so nested items are recorded as flattened. */
  let listDepth = 0;
  let listKind: 'bullet' | 'ordered' | null = null;
  let inBlockquote = 0;

  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];

    try {
      switch (t.type) {
        case 'bullet_list_open':
          listDepth += 1; listKind = 'bullet'; break;
        case 'ordered_list_open':
          listDepth += 1; listKind = 'ordered'; break;
        case 'bullet_list_close':
        case 'ordered_list_close':
          listDepth = Math.max(0, listDepth - 1);
          if (listDepth === 0) listKind = null;
          break;

        case 'blockquote_open': inBlockquote += 1; break;
        case 'blockquote_close': inBlockquote = Math.max(0, inBlockquote - 1); break;

        case 'hr':
          blocks.push(makeBlock('divider', []));
          break;

        case 'fence':
        case 'code_block': {
          const info = (t.info || '').trim().split(/\s+/)[0] || '';
          blocks.push(makeBlock('codeBlock',
            [{ type: 'text', text: t.content.replace(/\n$/, ''), styles: {} }],
            info ? { language: info } : {}));
          break;
        }

        case 'heading_open': {
          const level = Math.min(6, Math.max(1, Number(t.tag.slice(1)) || 1));
          const rich = inlineTokensToNodes(collectInline(tokens, i));
          blocks.push(makeBlock('heading', rich, { level }));
          i = skipInline(tokens, i);
          break;
        }

        case 'paragraph_open': {
          const inline = collectInline(tokens, i);
          // An image is an INLINE child, not a top-level token: markdown
          // emits `paragraph_open > inline > image > paragraph_close`. So a
          // paragraph whose whole body is one image is an image block.
          const img = inline.find((c) => c.type === 'image');
          if (img) {
            const src = img.attrGet('src') || '';
            const alt = img.content || '';
            if (src) {
              // F 208 D2 — the image node is ATOMIC in BlockNote. Probed, not
              // assumed: giving `image` inline text content makes
              // create() throw `RangeError: Invalid content for node image`,
              // while content: [] loads. The alt text already travels in
              // props.name, which is where BlockNote reads it, so the inline
              // copy was both wrong and redundant.
              blocks.push(makeBlock('image', [], { url: src, name: alt }));
            } else {
              blocks.push(makeBlock('paragraph', [{ type: 'text', text: alt || 'image', styles: {} }]));
              drops.add('image', 'raw-html-escaped-to-text',
                'An image with no src was parsed as a paragraph carrying its alt text.');
            }
            i = skipInline(tokens, i);
            break;
          }
          const rich = inlineTokensToNodes(inline);
          const text = plainText(rich);
          if (listDepth > 0 && inBlockquote === 0) {
            // A list item's paragraph.
            const task = TASK_RE.exec(text);
            if (task) {
              blocks.push(makeBlock('checkListItem',
                stripTaskPrefix(task[1], text),
                { checked: task[1].toLowerCase() === 'x' }));
            } else {
              blocks.push(makeBlock(listKind === 'ordered' ? 'numberedListItem' : 'bulletListItem',
                rich));
            }
            if (listDepth > 1) {
              drops.add('listItem', 'nested-list-flattened',
                'Nested list items were parsed as top-level siblings; markdown nesting depth is not represented on a Block.');
            }
          } else if (inBlockquote > 0) {
            blocks.push(makeBlock('quote', rich));
            drops.add('quote', 'blockquote-children-flattened',
              'Blockquote contents were parsed as sibling quote blocks; the grouping is not carried on the Block.');
          } else {
            blocks.push(makeBlock('paragraph', rich));
          }
          i = skipInline(tokens, i);
          break;
        }

        case 'image': {
          const src = t.attrGet('src') || '';
          // markdown-it puts the alt text in `content` for a standalone image token.
          const alt = t.content || (t.children?.map((c) => c.content ?? '').join('') || '');
          if (!src) {
            drops.add('image', 'raw-html-escaped-to-text',
              'An image with no src was parsed as a paragraph carrying its alt text.');
            blocks.push(makeBlock('paragraph', [{ type: 'text', text: alt || 'image', styles: {} }]));
          } else {
            // F 208 D2 — same atomic-content rule as the paragraph_open path.
            blocks.push(makeBlock('image', [], { url: src, name: alt }));
          }
          break;
        }

        case 'table_open': {
          // Cut 2b has no grid-shaped Block. Rows are preserved as paragraphs
          // carrying their cells, and the loss is reported rather than hidden.
          const rows = collectTableRows(tokens, i, drops);
          for (const cells of rows) {
            blocks.push(makeBlock('paragraph', [{ type: 'text', text: cells.join(' | '), styles: {} }]));
          }
          drops.add('table', 'table-degraded-to-rows',
            'Tables were parsed into one paragraph per row, cells joined with " | ". A Block has no grid shape in Cut 2b.');
          i = skipTable(tokens, i);
          break;
        }

        case 'html_block':
          // html:false escapes this into text, so it should not appear.
          blocks.push(makeBlock('paragraph', [{ type: 'text', text: t.content, styles: {} }]));
          drops.add('paragraph', 'raw-html-escaped-to-text',
            'Raw HTML was escaped to literal text rather than interpreted.');
          break;

        default:
          break;
      }
    } catch {
      // NEVER THROW: one bad token must not lose the document.
      drops.add('block', 'unknown-token-degraded-to-paragraph',
        'A construct could not be mapped to a Block; the surrounding text was kept as a paragraph.');
      blocks.push(makeBlock('paragraph', [{ type: 'text', text: t.content || '', styles: {} }]));
    }
  }

  if (blocks.length === 0 && markdown.trim().length > 0) {
    blocks.push(makeBlock('paragraph', [{ type: 'text', text: markdown.trim(), styles: {} }]));
    drops.add('paragraph', 'unknown-token-degraded-to-paragraph',
      'No construct was recognised; the source was kept as a single paragraph.');
  }
  return { blocks, drops: drops.list() };
}

/** `[ ] ` / `[x] ` are consumed by the task marker, not by the text. */
function stripTaskPrefix(_marker: string, text: string): InlineNode[] {
  return [{ type: 'text', text: text.replace(TASK_RE, ''), styles: {} }];
}

/**
 * Collect the inline tokens for a `_open` block.
 *
 * markdown-it does NOT put inline tokens at the top level. The block is
 * `paragraph_open` -> a SINGLE `inline` token -> `paragraph_close`, and the
 * real tokens (text, strong_open, link_open, …) live in that `inline` token's
 * `.children`. Scanning the top level for them returns an empty run, which
 * silently produces content-less blocks — the first version of this parser
 * did exactly that and every paragraph came back with no text.
 */
function collectInline(tokens: Token[], openIndex: number): Token[] {
  for (let j = openIndex + 1; j < tokens.length; j++) {
    if (tokens[j].type === 'paragraph_close' || tokens[j].type === 'heading_close') break;
    if (tokens[j].type === 'inline') return tokens[j].children ?? [];
  }
  return [];
}

/** Advance `i` past the inline run and the closing token. */
function skipInline(tokens: Token[], openIndex: number): number {
  for (let j = openIndex + 1; j < tokens.length; j++) {
    if (tokens[j].type === 'paragraph_close' || tokens[j].type === 'heading_close') return j;
  }
  return openIndex;
}

function skipTable(tokens: Token[], openIndex: number): number {
  for (let j = openIndex + 1; j < tokens.length; j++) {
    if (tokens[j].type === 'tbody_close' || tokens[j].type === 'table_close') return j;
  }
  return openIndex;
}

function collectTableRows(tokens: Token[], openIndex: number, drops: DropLog): string[][] {
  const rows: string[][] = [];
  let row: string[] | null = null;
  for (let j = openIndex + 1; j < tokens.length; j++) {
    const tk = tokens[j];
    if (tk.type === 'table_close') break;
    if (tk.type === 'tr_open') row = [];
    else if (tk.type === 'tr_close' && row) { rows.push(row); row = null; }
    else if (tk.type === 'td_open' || tk.type === 'th_open') row = row ?? [];
    else if ((tk.type === 'inline' || tk.type === 'text') && row) {
      if (row.length === 0 || row[row.length - 1] !== tk.content) row.push(tk.content);
    }
  }
  return rows;
}
